import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown, Check, Loader2, Lock, User, Phone, Mail, Tag, Hash, Eye, EyeOff, QrCode, AlertCircle, CheckCircle2, Zap, Copy, Download, ShieldAlert, Sparkles, Gift, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { AuthShell } from "../components/AuthShell";
import { AuthTabs } from "../components/AuthTabs";
import { BdFlag } from "../components/BdFlag";
import QRScanModal from "../components/QRScanModal";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

// Persist referral / affiliate code from the URL so attribution survives
// page navigation (e.g. user clicks share link → browses → then registers).
const REF_STORAGE_KEY = "mx88_ref_code";
const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function readStoredRefCode() {
  try {
    const raw = localStorage.getItem(REF_STORAGE_KEY);
    if (!raw) return "";
    const obj = JSON.parse(raw);
    if (!obj || !obj.code) return "";
    if (obj.ts && Date.now() - obj.ts > REF_TTL_MS) {
      localStorage.removeItem(REF_STORAGE_KEY);
      return "";
    }
    return String(obj.code || "");
  } catch {
    return "";
  }
}

function storeRefCode(code) {
  try {
    if (code) {
      localStorage.setItem(
        REF_STORAGE_KEY,
        JSON.stringify({ code: String(code).toUpperCase(), ts: Date.now() }),
      );
    }
  } catch {
    /* ignore quota / privacy errors */
  }
}

const Field = ({ label, icon: Icon, children }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-sm sm:text-base text-white/90 font-medium">
      {Icon && <Icon size={14} className="text-[#19a55c]" />}
      {label}
    </label>
    {children}
  </div>
);

// Tiny inline pill rendered under an input with the live-check verdict.
// Hoisted out of RegisterPage so React doesn't re-create the component on
// every render (preserves input focus + avoids `no-unstable-nested-components`).
const LiveStatus = ({ check, testid }) => {
  if (!check || check.status === "idle") return null;
  let cls = "text-white/55";
  let Icon = Loader2;
  let iconCls = "animate-spin";
  let label = "Checking availability…";
  if (check.status === "available") {
    cls = "text-emerald-300"; Icon = CheckCircle2; iconCls = ""; label = "Available";
  } else if (check.status === "taken") {
    cls = "text-rose-300"; Icon = AlertCircle; iconCls = ""; label = check.reason || "Already taken";
  } else if (check.status === "invalid") {
    cls = "text-rose-300"; Icon = AlertCircle; iconCls = ""; label = check.reason || "Not valid";
  }
  return (
    <div
      data-testid={testid}
      className={`mt-1.5 flex items-center gap-1.5 text-[12px] font-semibold ${cls}`}
    >
      <Icon size={13} className={iconCls} />
      <span>{label}</span>
    </div>
  );
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, quickRegister } = useAuth();
  const [searchParams] = useSearchParams();

  // ── Back-button trap ──
  // Handled centrally in GuestRoute (wraps this page) so it installs the moment
  // the user lands — even before the auth check resolves. See ProtectedRoute.jsx.

  // Pull ref code from URL (?ref=CODE, ?refer=CODE, ?ref_code=CODE,
  // ?promo=CODE, ?code=CODE, ?aff=CODE — any of the common variants).
  // Fall back to a previously stored code from a share-link visit.
  const initialRef = (
    searchParams.get("ref") ||
    searchParams.get("refer") ||
    searchParams.get("ref_code") ||
    searchParams.get("refer_code") ||
    searchParams.get("promo") ||
    searchParams.get("promo_code") ||
    searchParams.get("code") ||
    searchParams.get("aff") ||
    searchParams.get("affiliate") ||
    readStoredRefCode() ||
    ""
  )
    .toString()
    .trim()
    .toUpperCase();

  const [form, setForm] = useState({
    full_name: "",
    username: "",
    password: "",
    currency: "BDT",
    country_code: "+880",
    phone: "",
    email: "",
    refer_code: initialRef,
  });
  // Signup method tab: "phone" (default — best for BD users) | "email"
  const [signupMethod, setSignupMethod] = useState("phone");
  // Refer/Promo code panel stays open by default (auto-open regardless of URL code).
  const [showRefer, setShowRefer] = useState(true);
  const [accepted, setAccepted] = useState(true);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  // One-click signup state
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const [quickCreds, setQuickCreds] = useState(null); // { username, password, bonus }
  const [quickAck, setQuickAck] = useState(false);
  const [quickCopiedField, setQuickCopiedField] = useState("");
  const [quickShowPw, setQuickShowPw] = useState(false);
  const [recoveryBonus, setRecoveryBonus] = useState(0);

  // After the credentials modal opens, fetch recovery-bonus amount from the
  // backend so we can advertise it inside the downloaded PNG.
  useEffect(() => {
    if (!quickCreds) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/site/auth/recovery-bonus/status`,
          { credentials: "include" },
        );
        const data = await res.json();
        if (!cancelled && data?.eligible && data.bonus > 0) {
          setRecoveryBonus(Number(data.bonus) || 0);
        }
      } catch { /* ignore — banner just won't appear on the image */ }
    })();
    return () => { cancelled = true; };
  }, [quickCreds]);

  const runQuickSignup = async () => {
    setError("");
    setQuickSubmitting(true);
    const refCodeTrimmed = (form.refer_code || "").trim().toUpperCase();
    const res = await quickRegister(refCodeTrimmed || null);
    setQuickSubmitting(false);
    if (res.ok) {
      try { localStorage.removeItem(REF_STORAGE_KEY); } catch { /* ignore */ }
      setQuickCreds({ username: res.username, password: res.password, bonus: res.bonus || 0 });
    } else {
      const msg = res.error || "সাইন আপ করা যায়নি। আবার চেষ্টা করুন।";
      setError(msg);
      toast.error(msg);
    }
  };

  const copyToClipboard = async (text, fieldLabel) => {
    // Use native clipboard API when available; fall back to legacy execCommand
    // for older browsers / non-HTTPS previews (Emergent preview iframes).
    const labelBn = fieldLabel === "Username" ? "ইউজারনেম" : fieldLabel === "Password" ? "পাসওয়ার্ড" : fieldLabel;
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch { /* fall through to legacy */ }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        ta.style.pointerEvents = "none";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (ok) {
      setQuickCopiedField(fieldLabel);
      toast.success(`${labelBn} কপি হয়েছে`);
      setTimeout(() => setQuickCopiedField(""), 1500);
    } else {
      toast.error("কপি করা যায়নি — নিজে সিলেক্ট করে কপি করুন");
    }
  };

  const copyAllCreds = async () => {
    if (!quickCreds) return;
    const text = `Maxlive88 লগইন\nইউজারনেম: ${quickCreds.username}\nপাসওয়ার্ড: ${quickCreds.password}`;
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch { /* fall through */ }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (ok) toast.success("ইউজারনেম ও পাসওয়ার্ড কপি হয়েছে");
    else toast.error("কপি করা যায়নি");
  };

  const downloadCreds = async () => {
    if (!quickCreds) return;

    // Pre-generate the QR code (async) — points to /login?u=<username>&p=<pw>&auto=1
    // so scanning the image takes the user straight to Dashboard (one-tap login).
    // Password IS embedded in the QR — same threat model as the credentials
    // already shown as plaintext on the same image, and it enables the killer
    // "scan-to-login" UX. On the login page we scrub the URL immediately after
    // auto-submit so credentials don't linger in history / analytics.
    let qrDataUrl = null;
    try {
      const QRCode = (await import("qrcode")).default;
      const loginUrl =
        `${window.location.origin}/login?` +
        `u=${encodeURIComponent(quickCreds.username)}` +
        `&p=${encodeURIComponent(quickCreds.password)}` +
        `&auto=1`;
      qrDataUrl = await QRCode.toDataURL(loginUrl, {
        margin: 1,
        width: 300,
        color: { dark: "#0a1119", light: "#ffffff" },
        errorCorrectionLevel: "M",
      });
    } catch { /* QR is a bonus — still generate the image without it */ }

    const W = 1200;
    const H = 900;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Background — deep navy with subtle radial glow
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, "#0e1826");
    bg.addColorStop(1, "#050a12");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    const glow = ctx.createRadialGradient(W / 2, 0, 0, W / 2, 0, 700);
    glow.addColorStop(0, "rgba(25,165,92,0.28)");
    glow.addColorStop(1, "rgba(25,165,92,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, 400);

    const roundRect = (x, y, w, h, r, fill, stroke) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
      if (fill) { ctx.fillStyle = fill; ctx.fill(); }
      if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 1.5; ctx.stroke(); }
    };

    // Brand
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 42px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.textBaseline = "top";
    ctx.fillText("Maxlive", 60, 55);
    ctx.fillStyle = "#f5533d";
    ctx.fillText("88", 60 + ctx.measureText("Maxlive").width, 55);
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "500 15px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText("Play Smart · Win Big", 60, 108);

    // ACCOUNT ACTIVE pill
    const pillX = W - 60 - 210;
    const pillY = 62;
    roundRect(pillX, pillY, 210, 40, 20, "rgba(25,165,92,0.15)", "rgba(25,165,92,0.5)");
    ctx.fillStyle = "#3ee082";
    ctx.beginPath(); ctx.arc(pillX + 22, pillY + 20, 5, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#8ff0b7";
    ctx.font = "800 13px system-ui, sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText("ACCOUNT ACTIVE", pillX + 38, pillY + 20);

    // Divider
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(60, 155); ctx.lineTo(W - 60, 155); ctx.stroke();

    // Title
    ctx.fillStyle = "#ffffff";
    ctx.textBaseline = "top";
    ctx.font = "900 32px system-ui, sans-serif";
    ctx.fillText("Your Login Details", 60, 190);
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.font = "500 16px system-ui, sans-serif";
    ctx.fillText("Keep this image safe — it's the only way back into your account.", 60, 232);

    // Layout — credentials full-width; QR floats at bottom-right corner
    const leftX = 60;
    const leftW = W - 120;
    const cardH = 100;

    // Username card
    const uY = 285;
    roundRect(leftX, uY, leftW, cardH, 14, "#0b1220", "rgba(25,165,92,0.28)");
    ctx.fillStyle = "rgba(255,255,255,0.4)";
    ctx.font = "800 12px system-ui, sans-serif";
    ctx.fillText("USERNAME", leftX + 28, uY + 20);
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 30px ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
    ctx.fillText(quickCreds.username || "", leftX + 28, uY + 46);

    // Password card
    const pY = uY + cardH + 20;
    roundRect(leftX, pY, leftW, cardH, 14, "#0b1220", "rgba(25,165,92,0.28)");
    ctx.fillStyle = "rgba(255,255,255,0.4)";
    ctx.font = "800 12px system-ui, sans-serif";
    ctx.fillText("PASSWORD", leftX + 28, pY + 20);
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 30px ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
    ctx.fillText(quickCreds.password || "", leftX + 28, pY + 46);

    // Warning row — full width, below password
    const wY = pY + cardH + 26;
    const wW = W - 120;
    roundRect(60, wY, wW, 70, 12, "rgba(255,193,7,0.08)", "rgba(255,193,7,0.32)");
    ctx.fillStyle = "#ffc933";
    ctx.beginPath();
    ctx.moveTo(94, wY + 22);
    ctx.lineTo(112, wY + 50);
    ctx.lineTo(76, wY + 50);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#0e1826";
    ctx.font = "900 20px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("!", 94, wY + 30);
    ctx.textAlign = "left";
    ctx.fillStyle = "#ffe28a";
    ctx.font = "700 15px system-ui, sans-serif";
    ctx.fillText("Save this image now — no phone / email means no account recovery.", 134, wY + 26);

    // Recovery bonus offer + QR (bottom row — side by side)
    if (recoveryBonus > 0) {
      const bY = wY + 70 + 26;
      const bH = 130;
      // If QR is available, leave room on the right for the QR card
      const qrReserve = qrDataUrl ? 180 : 0; // 160 QR + 20 gap
      const bW = W - 120 - qrReserve;

      // Gradient fill
      const bGrad = ctx.createLinearGradient(60, bY, 60 + bW, bY);
      bGrad.addColorStop(0, "rgba(255,201,51,0.18)");
      bGrad.addColorStop(0.55, "rgba(245,83,61,0.14)");
      bGrad.addColorStop(1, "rgba(20,15,25,0.4)");
      roundRect(60, bY, bW, bH, 16, null, null);
      ctx.fillStyle = bGrad;
      ctx.fill();
      roundRect(60, bY, bW, bH, 16, null, "rgba(255,201,51,0.4)");

      // ---- Shield icon (proper, with check mark inside) ----
      const iconSize = 64;
      const iconX = 92;
      const iconY = bY + (bH - iconSize) / 2;
      const cx = iconX + iconSize / 2;
      const topY = iconY + 2;
      const bottomY = iconY + iconSize - 2;
      const leftE = iconX + 3;
      const rightE = iconX + iconSize - 3;
      const shoulderY = iconY + iconSize * 0.42;

      // Outer shield gradient
      const shieldGrad = ctx.createLinearGradient(iconX, iconY, iconX, bottomY);
      shieldGrad.addColorStop(0, "#ffd766");
      shieldGrad.addColorStop(1, "#f5773a");

      ctx.beginPath();
      ctx.moveTo(cx, topY);
      ctx.lineTo(rightE, iconY + iconSize * 0.16);
      ctx.lineTo(rightE, shoulderY);
      ctx.quadraticCurveTo(rightE, iconY + iconSize * 0.82, cx, bottomY);
      ctx.quadraticCurveTo(leftE, iconY + iconSize * 0.82, leftE, shoulderY);
      ctx.lineTo(leftE, iconY + iconSize * 0.16);
      ctx.closePath();
      ctx.fillStyle = shieldGrad;
      ctx.fill();

      // Inner highlight ring
      ctx.strokeStyle = "rgba(255,255,255,0.35)";
      ctx.lineWidth = 1.4;
      ctx.stroke();

      // Check mark inside shield
      ctx.strokeStyle = "#0a1119";
      ctx.lineWidth = 5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(cx - 12, iconY + iconSize * 0.52);
      ctx.lineTo(cx - 3, iconY + iconSize * 0.64);
      ctx.lineTo(cx + 14, iconY + iconSize * 0.42);
      ctx.stroke();
      ctx.lineCap = "butt";
      ctx.lineJoin = "miter";

      // LIMITED OFFER pill
      const bonusPillX = iconX + iconSize + 24;
      const bonusPillY = bY + 24;
      roundRect(bonusPillX, bonusPillY, 200, 26, 13, "rgba(255,201,51,0.15)", "rgba(255,201,51,0.5)");
      ctx.fillStyle = "#ffe28a";
      ctx.font = "800 11px system-ui, sans-serif";
      ctx.textBaseline = "middle";
      ctx.fillText("★  LIMITED OFFER", bonusPillX + 14, bonusPillY + 13);

      // Headline
      ctx.textBaseline = "top";
      ctx.fillStyle = "#ffffff";
      ctx.font = "900 24px system-ui, sans-serif";
      const head1 = "Secure your account & get ";
      ctx.fillText(head1, bonusPillX, bonusPillY + 36);
      const head1W = ctx.measureText(head1).width;
      ctx.fillStyle = "#ffc933";
      ctx.fillText(`৳${Number(recoveryBonus).toFixed(0)} bonus`, bonusPillX + head1W, bonusPillY + 36);

      // Sub line
      ctx.fillStyle = "rgba(255,255,255,0.65)";
      ctx.font = "500 14px system-ui, sans-serif";
      ctx.fillText(
        "Link a phone or email in Profile → Verification and we'll credit it instantly.",
        bonusPillX,
        bonusPillY + 72,
      );

      // ---- QR at bottom-right corner (aligned with bonus banner) ----
      if (qrDataUrl) {
        const qrCardW = 160;
        const qrCardH2 = 160;
        const qrX = W - 60 - qrCardW;
        const qrY = bY + (bH - qrCardH2) / 2 + 8; // slightly lower so caption fits below
        // Anchor QR card so it visually connects with bonus banner top
        const qrTop = bY;
        roundRect(qrX, qrTop, qrCardW, qrCardH2, 14, "#ffffff", "rgba(255,255,255,0.35)");
        const qrImg = new Image();
        await new Promise((resolve) => {
          qrImg.onload = resolve;
          qrImg.onerror = resolve;
          qrImg.src = qrDataUrl;
        });
        const qrPad = 12;
        const qrSize = qrCardW - qrPad * 2;
        ctx.drawImage(qrImg, qrX + qrPad, qrTop + qrPad, qrSize, qrSize);
        // Caption below the QR card (on dark bg)
        ctx.fillStyle = "#ffffff";
        ctx.font = "800 13px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillText("Scan Me", qrX + qrCardW / 2, qrTop + qrCardH2 + 10);
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.font = "500 10.5px system-ui, sans-serif";
        ctx.fillText("QR স্ক্যান করে সরাসরি Login করুন", qrX + qrCardW / 2, qrTop + qrCardH2 + 28);
        ctx.textAlign = "left";
        // Silence unused
        void qrY;
      }
    } else if (qrDataUrl) {
      // No bonus banner — still show QR alone at bottom-right
      const bY = wY + 70 + 26;
      const qrCardW = 160;
      const qrCardH2 = 160;
      const qrX = W - 60 - qrCardW;
      roundRect(qrX, bY, qrCardW, qrCardH2, 14, "#ffffff", "rgba(255,255,255,0.35)");
      const qrImg = new Image();
      await new Promise((resolve) => {
        qrImg.onload = resolve;
        qrImg.onerror = resolve;
        qrImg.src = qrDataUrl;
      });
      const qrPad = 12;
      const qrSize = qrCardW - qrPad * 2;
      ctx.drawImage(qrImg, qrX + qrPad, bY + qrPad, qrSize, qrSize);
      ctx.fillStyle = "#ffffff";
      ctx.font = "800 13px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText("Scan Me", qrX + qrCardW / 2, bY + qrCardH2 + 10);
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.font = "500 10.5px system-ui, sans-serif";
      ctx.fillText("QR স্ক্যান করে সরাসরি Login করুন", qrX + qrCardW / 2, bY + qrCardH2 + 28);
      ctx.textAlign = "left";
    }

    // Footer
    const fY = H - 60;
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = "500 13px system-ui, sans-serif";
    const dt = new Date();
    const stamp = dt.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
    ctx.fillText(`Generated: ${stamp}`, 60, fY);
    ctx.textAlign = "right";
    ctx.fillText("maxlive88.com  ·  24/7 Live Chat support", W - 60, fY);
    ctx.textAlign = "left";

    // Export
    try {
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `maxlive88-login-${quickCreds.username}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("লগইন ইমেজ ডাউনলোড হয়েছে");
    } catch {
      toast.error("ছবি সেভ করা যায়নি");
    }
  };

  const finishQuickSignup = () => {
    if (!quickAck) {
      toast.error("আগে নিশ্চিত করুন যে আপনি ইউজারনেম ও পাসওয়ার্ড সেভ করেছেন");
      return;
    }
    const name = quickCreds?.username || "there";
    setQuickCreds(null);
    toast.success("Maxlive88-এ স্বাগতম!", { description: `হ্যালো ${name} 👋` });
    navigate("/dashboard", { replace: true });
  };

  // ─── Live availability checks (username + phone + email) ─────────────
  // status: "idle" | "checking" | "available" | "taken" | "invalid"
  const [usernameCheck, setUsernameCheck] = useState({ status: "idle", reason: "", suggestions: [] });
  const [phoneCheck, setPhoneCheck] = useState({ status: "idle", reason: "" });
  const [emailCheck, setEmailCheck] = useState({ status: "idle", reason: "" });
  const usernameAbortRef = useRef(null);
  const phoneAbortRef = useRef(null);
  const emailAbortRef = useRef(null);

  // Debounced username availability check
  useEffect(() => {
    const raw = (form.username || "").trim();
    if (!raw) {
      setUsernameCheck({ status: "idle", reason: "", suggestions: [] });
      return undefined;
    }
    setUsernameCheck((s) => ({ ...s, status: "checking", reason: "" }));
    const t = setTimeout(async () => {
      try {
        if (usernameAbortRef.current) usernameAbortRef.current.abort();
        const ctrl = new AbortController();
        usernameAbortRef.current = ctrl;
        const res = await fetch(
          `${API_BASE}/auth/check-username?username=${encodeURIComponent(raw)}`,
          { signal: ctrl.signal },
        );
        const data = await res.json();
        if (data && data.available) {
          setUsernameCheck({ status: "available", reason: "", suggestions: [] });
        } else {
          const r = (data && data.reason) || "Not available";
          const sug = Array.isArray(data?.suggestions) ? data.suggestions : [];
          // Distinguish format errors (e.g. "must be 3-24 characters") from
          // uniqueness collisions ("already taken") so the pill colour is right.
          const isTaken = /taken|registered|already/i.test(r);
          setUsernameCheck({ status: isTaken ? "taken" : "invalid", reason: r, suggestions: sug });
        }
      } catch (e) {
        if (e && e.name === "AbortError") return;
        setUsernameCheck({ status: "idle", reason: "", suggestions: [] });
      }
    }, 380);
    return () => clearTimeout(t);
  }, [form.username]);

  // Debounced phone availability check
  useEffect(() => {
    const raw = (form.phone || "").replace(/[\s-]/g, "");
    if (!raw) {
      setPhoneCheck({ status: "idle", reason: "" });
      return undefined;
    }
    setPhoneCheck({ status: "checking", reason: "" });
    const t = setTimeout(async () => {
      try {
        if (phoneAbortRef.current) phoneAbortRef.current.abort();
        const ctrl = new AbortController();
        phoneAbortRef.current = ctrl;
        const res = await fetch(
          `${API_BASE}/auth/check-phone?phone=${encodeURIComponent(raw)}&country_code=${encodeURIComponent(form.country_code || "+880")}`,
          { signal: ctrl.signal },
        );
        const data = await res.json();
        if (data && data.available) {
          setPhoneCheck({ status: "available", reason: "" });
        } else {
          const r = (data && data.reason) || "Not available";
          const isTaken = /registered|taken|already/i.test(r);
          setPhoneCheck({ status: isTaken ? "taken" : "invalid", reason: r });
        }
      } catch (e) {
        if (e && e.name === "AbortError") return;
        setPhoneCheck({ status: "idle", reason: "" });
      }
    }, 420);
    return () => clearTimeout(t);
  }, [form.phone, form.country_code]);

  // Debounced email availability check
  useEffect(() => {
    const raw = (form.email || "").trim();
    if (!raw) {
      setEmailCheck({ status: "idle", reason: "" });
      return undefined;
    }
    setEmailCheck({ status: "checking", reason: "" });
    const t = setTimeout(async () => {
      try {
        if (emailAbortRef.current) emailAbortRef.current.abort();
        const ctrl = new AbortController();
        emailAbortRef.current = ctrl;
        const res = await fetch(
          `${API_BASE}/auth/check-email?email=${encodeURIComponent(raw)}`,
          { signal: ctrl.signal },
        );
        const data = await res.json();
        if (data && data.available) {
          setEmailCheck({ status: "available", reason: "" });
        } else {
          const r = (data && data.reason) || "Not available";
          const isTaken = /registered|taken|already/i.test(r);
          setEmailCheck({ status: isTaken ? "taken" : "invalid", reason: r });
        }
      } catch (e) {
        if (e && e.name === "AbortError") return;
        setEmailCheck({ status: "idle", reason: "" });
      }
    }, 420);
    return () => clearTimeout(t);
  }, [form.email]);

  // ─── Email OTP verification (only used on email signup path) ────────
  const EMAIL_DOMAINS = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com", "icloud.com", "proton.me"];
  const [emailOtp, setEmailOtp] = useState({
    sent: false,
    verified: false,
    sending: false,
    verifying: false,
    code: ["", "", "", "", "", ""],
    cooldown: 0,
    expiresIn: 0,
    devCode: "",
    error: "",
  });
  const otpCellRefs = useRef([]);
  const [emailSuggestOpen, setEmailSuggestOpen] = useState(false);

  // Cooldown ticker (resend gate, 60s)
  useEffect(() => {
    if (emailOtp.cooldown <= 0) return undefined;
    const t = setTimeout(
      () => setEmailOtp((s) => ({ ...s, cooldown: Math.max(0, s.cooldown - 1) })),
      1000,
    );
    return () => clearTimeout(t);
  }, [emailOtp.cooldown]);

  // Expiry ticker (10-minute window, counts down to 0 then resets sent state)
  useEffect(() => {
    if (emailOtp.expiresIn <= 0 || emailOtp.verified) return undefined;
    const t = setTimeout(
      () => setEmailOtp((s) => {
        const next = Math.max(0, s.expiresIn - 1);
        if (next === 0) {
          return { ...s, expiresIn: 0, sent: false, code: ["", "", "", "", "", ""], error: "Code expired. Please request a new one." };
        }
        return { ...s, expiresIn: next };
      }),
      1000,
    );
    return () => clearTimeout(t);
  }, [emailOtp.expiresIn, emailOtp.verified]);

  // Whenever the email changes, drop any prior OTP verification state
  useEffect(() => {
    setEmailOtp((s) => ({ ...s, sent: false, verified: false, code: ["", "", "", "", "", ""], devCode: "", error: "", expiresIn: 0 }));
  }, [form.email]);

  // Reset OTP state when switching method tabs
  useEffect(() => {
    setEmailOtp({ sent: false, verified: false, sending: false, verifying: false, code: ["", "", "", "", "", ""], cooldown: 0, expiresIn: 0, devCode: "", error: "" });
  }, [signupMethod]);

  const emailDomainSuggestions = (() => {
    const raw = (form.email || "").trim();
    if (!raw) return [];
    const at = raw.indexOf("@");
    if (at < 0) return EMAIL_DOMAINS.map((d) => `${raw}@${d}`);
    const local = raw.slice(0, at);
    const partial = raw.slice(at + 1).toLowerCase();
    if (!local) return [];
    if (EMAIL_DOMAINS.includes(partial)) return [];
    return EMAIL_DOMAINS.filter((d) => d.startsWith(partial)).map((d) => `${local}@${d}`);
  })();

  const emailIsValid = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(
    (form.email || "").trim().toLowerCase(),
  );

  const fmtMmSs = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const sendEmailOtp = async () => {
    const email = (form.email || "").trim().toLowerCase();
    if (!emailIsValid) {
      setEmailOtp((s) => ({ ...s, error: "Enter a valid email first" }));
      return;
    }
    setEmailOtp((s) => ({ ...s, sending: true, error: "" }));
    try {
      const res = await fetch(`${API_BASE}/auth/signup/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.detail || "Failed to send OTP";
        setEmailOtp((s) => ({ ...s, sending: false, error: msg }));
        toast.error(msg);
        return;
      }
      setEmailOtp((s) => ({
        ...s,
        sent: true,
        sending: false,
        cooldown: 60,
        expiresIn: data?.expires_in || 600,
        devCode: data?.dev_code || "",
        code: ["", "", "", "", "", ""],
        error: "",
      }));
      if (data?.email_sent) {
        toast.success("Verification code sent to your email");
      } else if (data?.dev_code) {
        toast.success(`OTP: ${data.dev_code} (gateway not configured)`, { duration: 8000 });
      } else {
        toast.success("Verification code sent");
      }
      // Focus first cell
      setTimeout(() => otpCellRefs.current[0]?.focus(), 80);
    } catch (e) {
      setEmailOtp((s) => ({ ...s, sending: false, error: "Network error. Try again." }));
    }
  };

  const verifyEmailOtp = async (codeOverride) => {
    const email = (form.email || "").trim().toLowerCase();
    const code = (codeOverride || emailOtp.code.join("") || "").trim();
    if (!/^\d{6}$/.test(code)) {
      setEmailOtp((s) => ({ ...s, error: "Enter the 6-digit code" }));
      return;
    }
    setEmailOtp((s) => ({ ...s, verifying: true, error: "" }));
    try {
      const res = await fetch(`${API_BASE}/auth/signup/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.detail || "Invalid code";
        setEmailOtp((s) => ({ ...s, verifying: false, error: msg }));
        toast.error(msg);
        return;
      }
      setEmailOtp((s) => ({ ...s, verifying: false, verified: true, expiresIn: 0, error: "" }));
      toast.success("Email verified ✓");
    } catch (e) {
      setEmailOtp((s) => ({ ...s, verifying: false, error: "Network error. Try again." }));
    }
  };

  // OTP cell handlers — auto-advance + paste support
  const handleOtpCellChange = (idx, v) => {
    const digit = (v || "").replace(/\D/g, "").slice(-1);
    setEmailOtp((s) => {
      const next = [...s.code];
      next[idx] = digit;
      const full = next.join("");
      // Auto-verify on full 6-digit entry
      if (digit && idx === 5 && full.length === 6 && !s.verifying) {
        setTimeout(() => verifyEmailOtp(full), 50);
      }
      return { ...s, code: next, error: "" };
    });
    if (digit && idx < 5) {
      setTimeout(() => otpCellRefs.current[idx + 1]?.focus(), 0);
    }
  };

  const handleOtpCellKey = (idx, e) => {
    if (e.key === "Backspace" && !emailOtp.code[idx] && idx > 0) {
      otpCellRefs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowLeft" && idx > 0) {
      otpCellRefs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      otpCellRefs.current[idx + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const txt = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const arr = txt.split("");
    while (arr.length < 6) arr.push("");
    setEmailOtp((s) => ({ ...s, code: arr.slice(0, 6), error: "" }));
    const lastFilled = Math.min(txt.length - 1, 5);
    setTimeout(() => otpCellRefs.current[lastFilled]?.focus(), 0);
    if (txt.length === 6) setTimeout(() => verifyEmailOtp(txt), 50);
  };

  // Parser for QR codes: accepts either a raw code string (e.g. "RAHIM2026")
  // or a full Maxlive88 invite URL (e.g. "https://maxlive88.com/register?ref=RAHIM2026").
  // Returns the extracted code or null if nothing usable is found.
  const parseScannedQR = (text) => {
    if (!text) return null;
    const raw = String(text).trim();
    // Try URL parse first
    try {
      const u = new URL(raw);
      const code =
        u.searchParams.get("ref") ||
        u.searchParams.get("refer") ||
        u.searchParams.get("refer_code") ||
        u.searchParams.get("ref_code") ||
        u.searchParams.get("promo") ||
        u.searchParams.get("promo_code") ||
        u.searchParams.get("code") ||
        u.searchParams.get("aff");
      if (code) return code.toUpperCase();
    } catch {
      /* not a URL — fall through to raw-code path */
    }
    // Plain code path — accept 4–24 chars of [A-Z0-9_-] (server validates further)
    const cleaned = raw.toUpperCase().replace(/[^A-Z0-9_-]/g, "");
    if (cleaned.length >= 4 && cleaned.length <= 24) return cleaned;
    return null;
  };

  // Persist the incoming ref code so it survives navigation/refresh
  useEffect(() => {
    if (initialRef) storeRefCode(initialRef);
  }, [initialRef]);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  // Phone-specific change handler — strips non-digits and clamps to the
  // country's max length so the user PHYSICALLY can't type an invalid
  // number. For +880 that means at most 11 digits.
  const onPhoneChange = (e) => {
    const cc = form.country_code || "+880";
    const maxLen = cc === "+880" ? 11 : 15;
    const cleaned = (e.target.value || "").replace(/\D/g, "").slice(0, maxLen);
    setForm((s) => ({ ...s, phone: cleaned }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!accepted) {
      setError("You must confirm you are 18+ and accept the Terms & Conditions");
      return;
    }
    // Client-side field validation matching backend rules
    const fullName = (form.full_name || "").trim();
    const username = (form.username || "").trim();
    const password = form.password || "";
    let phone = (form.phone || "").replace(/[\s-]/g, "");

    if (fullName.length < 2 || fullName.length > 60) {
      setError("Full name must be 2-60 characters");
      return;
    }
    if (username.length < 3 || username.length > 24) {
      setError("Username must be 3-24 characters");
      return;
    }
    if (usernameCheck.status === "taken") {
      setError("This username is already taken");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    // Branch: phone vs email signup (mutually exclusive paths).
    let phoneToSubmit = "";
    let emailToSubmit = "";
    if (signupMethod === "phone") {
      // Auto-prepend leading 0 for Bangladeshi numbers if user forgot
      if (form.country_code === "+880" && /^1[3-9]\d{8}$/.test(phone)) {
        phone = "0" + phone;
      }
      if (!/^\d+$/.test(phone)) {
        setError("Phone number must contain only digits");
        return;
      }
      if (form.country_code === "+880") {
        if (phone.length < 10 || phone.length > 11) {
          setError("Bangladeshi phone must be 10 or 11 digits (e.g. 01712345678)");
          return;
        }
        if (phone.length === 11 && !phone.startsWith("0")) {
          setError("11-digit Bangladeshi phone must start with 0 (e.g. 01712345678)");
          return;
        }
        if (phone.length === 10 && !/^1[3-9]\d{8}$/.test(phone)) {
          setError("10-digit Bangladeshi phone must start with 13–19 (e.g. 1712345678)");
          return;
        }
      } else if (phone.length < 6 || phone.length > 15) {
        setError("Please enter a valid phone number");
        return;
      }
      if (phoneCheck.status === "taken") {
        setError("This phone number is already registered");
        return;
      }
      phoneToSubmit = phone;
    } else {
      // Email signup
      const emailTrim = (form.email || "").trim().toLowerCase();
      if (!emailTrim) {
        setError("Please enter your email address");
        return;
      }
      if (!/^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(emailTrim)) {
        setError("Please enter a valid email address");
        return;
      }
      if (emailCheck.status === "taken") {
        setError("This email is already registered");
        return;
      }
      if (!emailOtp.verified) {
        setError("Please verify your email with the OTP code before continuing");
        return;
      }
      emailToSubmit = emailTrim;
    }
    if (!captchaToken) {
      setError("অনুগ্রহ করে নিচের বক্সে ভেরিফিকেশন (আমি রোবট নই) সম্পূর্ণ করুন।");
      return;
    }
    setSubmitting(true);
    const refCodeTrimmed = (form.refer_code || "").trim().toUpperCase();
    const payload = {
      full_name: fullName,
      username,
      password,
      currency: form.currency,
      country_code: form.country_code,
      phone: phoneToSubmit,
      email: emailToSubmit,
      refer_code: refCodeTrimmed || null,
      accept_terms: accepted,
      turnstile_token: captchaToken,
    };
    const res = await register(payload);
    setSubmitting(false);
    if (res.ok) {
      // Clear stored ref code after successful signup so it doesn't leak
      // into the next person who registers on this device.
      try { localStorage.removeItem(REF_STORAGE_KEY); } catch { /* ignore */ }
      toast.success("Welcome to Maxlive88!", { description: `Hi ${fullName.split(" ")[0]} 👋` });
      navigate("/dashboard", { replace: true });
    } else {
      const msg = res.error || "Registration failed. Please try again.";
      setError(msg);
      toast.error(msg);
      // Turnstile tokens are single-use — reset so the user gets a fresh one.
      setCaptchaToken("");
      setCaptchaReset((n) => n + 1);
    }
  };

  const inputCls =
    "w-full h-12 rounded-lg px-4 bg-[#1a2030] text-white placeholder:text-white/35 border border-white/5 focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/30 transition-all";

  // Build a colored ring class for inputs based on live-check status
  const ringFor = (status) => {
    if (status === "available") return "border-emerald-500/70 focus:border-emerald-500 focus:ring-emerald-500/30";
    if (status === "taken" || status === "invalid") return "border-rose-500/70 focus:border-rose-500 focus:ring-rose-500/30";
    if (status === "checking") return "border-amber-400/70 focus:border-amber-400 focus:ring-amber-400/30";
    return "";
  };

  return (
    <AuthShell variant="green">
      <AuthTabs active="signup" />

      {/* One-Click Sign Up — creates a guest account instantly with auto-generated credentials */}
      <div className="mb-5" data-testid="register-quick-signup-block">
        <button
          type="button"
          data-testid="register-quick-signup-btn"
          onClick={runQuickSignup}
          disabled={quickSubmitting || !accepted}
          className="group relative w-full h-14 rounded-xl overflow-hidden bg-gradient-to-r from-[#ffc933] via-[#ff9a3c] to-[#f5533d] text-black font-black text-base sm:text-lg shadow-[0_10px_30px_-8px_rgba(255,154,60,0.7)] hover:shadow-[0_14px_40px_-6px_rgba(255,154,60,0.9)] active:scale-[0.985] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ fontFamily: '"Hind Siliguri","Noto Sans Bengali",system-ui,sans-serif' }}
        >
          <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full duration-700" />
          <span className="relative inline-flex items-center justify-center gap-2.5">
            {quickSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>অ্যাকাউন্ট তৈরি হচ্ছে…</span>
              </>
            ) : (
              <>
                <Zap size={20} className="drop-shadow" />
                <span>এক ক্লিকে সাইন আপ · এখনই খেলুন</span>
              </>
            )}
          </span>
        </button>
        <p
          className="mt-2 text-center text-[11.5px] leading-snug text-white/55"
          style={{ fontFamily: '"Hind Siliguri","Noto Sans Bengali",system-ui,sans-serif' }}
        >
          ফোন / ইমেইল লাগবে না — আমরা আপনার জন্য username &amp; password তৈরি করে দিচ্ছি।
          {!accepted && (
            <span className="block text-amber-300 mt-0.5">
              চালু করতে নিচের ১৮+ ও শর্তাবলি বক্সে টিক দিন
            </span>
          )}
        </p>
      </div>

      {/* Divider — "or fill in the details" */}
      <div className="relative flex items-center gap-3 mb-5" aria-hidden="true">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
        <span
          className="text-[11px] font-bold tracking-widest text-white/40 uppercase"
          style={{ fontFamily: '"Hind Siliguri","Noto Sans Bengali",system-ui,sans-serif' }}
        >
          অথবা নিজে পূরণ করুন
        </span>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      </div>

      <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
        <Field label="Full Name" icon={User}>
          <input
            data-testid="register-fullname-input"
            type="text"
            placeholder="e.g. Rahim Uddin"
            value={form.full_name}
            onChange={onChange("full_name")}
            className={inputCls}
            autoComplete="name"
          />
        </Field>

        <Field label="Username" icon={Hash}>
          <input
            data-testid="register-username-input"
            type="text"
            placeholder="Pick a unique username"
            value={form.username}
            onChange={onChange("username")}
            className={`${inputCls} ${ringFor(usernameCheck.status)}`}
            autoComplete="username"
          />
          <LiveStatus check={usernameCheck} testid="register-username-status" />
          {usernameCheck.status === "taken" && usernameCheck.suggestions?.length > 0 && (
            <div
              data-testid="register-username-suggestions"
              className="mt-2 flex flex-wrap items-center gap-1.5"
            >
              <span className="text-[11.5px] font-semibold text-white/55 mr-0.5">
                Try:
              </span>
              {usernameCheck.suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`register-username-suggestion-${s}`}
                  onClick={() => setForm((f) => ({ ...f, username: s }))}
                  className="px-2.5 py-1 rounded-full text-[12px] font-semibold bg-emerald-500/15 text-emerald-200 border border-emerald-400/30 hover:bg-emerald-500/25 hover:border-emerald-400/60 active:scale-95 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </Field>

        <Field label="Password" icon={Lock}>
          <div className="relative">
            <input
              data-testid="register-password-input"
              type={showPassword ? "text" : "password"}
              placeholder="Create a password (min 6)"
              value={form.password}
              onChange={onChange("password")}
              className={`${inputCls} pr-12`}
              autoComplete="new-password"
            />
            <button
              type="button"
              data-testid="register-password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/55 hover:text-[#19a55c] transition-colors p-1"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </Field>

        {/* Currency — locked to BDT */}
        <Field label="Choose currency">
          <div
            data-testid="register-currency-locked"
            className="w-full h-12 rounded-lg px-4 flex items-center gap-3 bg-[#1a2030] border border-white/5 cursor-not-allowed"
          >
            <BdFlag size={22} />
            <span className="text-white font-semibold flex-1">BDT</span>
            <Lock size={14} className="text-white/40" />
          </div>
        </Field>

        {/* Method tabs — Phone is the DEFAULT (best for BD users); Email is the alternative */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm sm:text-base text-white/90 font-medium">
            Sign up with
          </label>
          <div
            role="tablist"
            className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-[#1a2030] border border-white/5"
            data-testid="register-method-tabs"
          >
            <button
              type="button"
              role="tab"
              aria-selected={signupMethod === "phone"}
              data-testid="register-method-phone"
              onClick={() => setSignupMethod("phone")}
              className={`h-10 rounded-md inline-flex items-center justify-center gap-2 text-sm font-bold transition ${
                signupMethod === "phone"
                  ? "bg-[#19a55c] text-white shadow-[0_2px_10px_rgba(25,165,92,0.45)]"
                  : "text-white/65 hover:text-white"
              }`}
            >
              <Phone size={14} /> Phone
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={signupMethod === "email"}
              data-testid="register-method-email"
              onClick={() => setSignupMethod("email")}
              className={`h-10 rounded-md inline-flex items-center justify-center gap-2 text-sm font-bold transition ${
                signupMethod === "email"
                  ? "bg-[#19a55c] text-white shadow-[0_2px_10px_rgba(25,165,92,0.45)]"
                  : "text-white/65 hover:text-white"
              }`}
            >
              <Mail size={14} /> Email
            </button>
          </div>
          <p className="text-[11.5px] text-white/45 leading-snug">
            {signupMethod === "phone"
              ? "Recommended — sign up with your mobile number"
              : "Alternative — sign up with your email address"}
          </p>
        </div>

        {signupMethod === "phone" ? (
          <Field label="Phone number" icon={Phone}>
            <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[120px_1fr] gap-2">
              <div className="relative h-12 rounded-lg bg-[#1a2030] flex items-center px-3 border border-white/5">
                <BdFlag size={20} />
                <span className="ml-2 text-white">{form.country_code}</span>
                <ChevronDown size={16} className="ml-auto text-white/50" />
              </div>
              <input
                data-testid="register-phone-input"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="01XXXXXXXXX (11 digits)"
                maxLength={form.country_code === "+880" ? 11 : 15}
                value={form.phone}
                onChange={onPhoneChange}
                className={`${inputCls} ${ringFor(phoneCheck.status)}`}
                autoComplete="tel"
              />
            </div>
            <LiveStatus check={phoneCheck} testid="register-phone-status" />
          </Field>
        ) : (
          <Field label="Email address" icon={Mail}>
            <div className="relative">
              <input
                data-testid="register-email-input"
                type="email"
                inputMode="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => {
                  const v = (e.target.value || "").replace(/\s+/g, "").toLowerCase();
                  setForm((s) => ({ ...s, email: v }));
                  setEmailSuggestOpen(true);
                }}
                onFocus={() => setEmailSuggestOpen(true)}
                onBlur={() => setTimeout(() => setEmailSuggestOpen(false), 150)}
                disabled={emailOtp.verified}
                className={`${inputCls} ${emailOtp.verified ? "pr-[110px]" : ""} ${ringFor(emailOtp.verified ? "available" : emailCheck.status)} ${emailOtp.verified ? "opacity-90 cursor-not-allowed" : ""}`}
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
              />
              {emailOtp.verified && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/35 text-emerald-300 text-[11px] font-bold uppercase tracking-wide">
                  <CheckCircle2 size={12} /> Verified
                </span>
              )}

              {/* Domain suggestion dropdown */}
              {emailSuggestOpen && !emailOtp.verified && emailDomainSuggestions.length > 0 && (
                <div
                  data-testid="register-email-suggestions"
                  className="absolute left-0 right-0 top-full mt-1 z-20 bg-[#0f1622] border border-white/10 rounded-lg shadow-xl overflow-hidden"
                >
                  {emailDomainSuggestions.slice(0, 5).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setForm((f) => ({ ...f, email: s }));
                        setEmailSuggestOpen(false);
                      }}
                      data-testid={`register-email-suggestion-${s.split('@')[1]}`}
                      className="w-full text-left px-3 py-2 text-[13px] text-white/80 hover:bg-[#19a55c]/15 hover:text-white transition flex items-center gap-2"
                    >
                      <Mail size={12} className="text-[#19a55c]/70" />
                      <span className="truncate">{s}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <LiveStatus check={emailCheck} testid="register-email-status" />

            {/* Prominent Send Verification Code button — full-width, below input */}
            {!emailOtp.verified && (
              <button
                type="button"
                data-testid="register-email-send-otp-btn"
                onClick={sendEmailOtp}
                disabled={
                  emailOtp.sending ||
                  emailOtp.cooldown > 0 ||
                  !emailIsValid ||
                  emailCheck.status === "taken"
                }
                className={`mt-3 w-full h-12 rounded-lg font-bold text-[14.5px] tracking-wide transition-all flex items-center justify-center gap-2.5 disabled:cursor-not-allowed ${
                  emailOtp.sent
                    ? "bg-[#1a2030] border border-[#19a55c]/40 text-[#21c46e] hover:bg-[#222a3d] disabled:opacity-50"
                    : "bg-gradient-to-r from-[#19a55c] to-[#21c46e] hover:from-[#21c46e] hover:to-[#2bd87b] text-white shadow-[0_6px_20px_rgba(25,165,92,0.35)] hover:shadow-[0_8px_26px_rgba(25,165,92,0.5)] active:shadow-[0_3px_10px_rgba(25,165,92,0.35)] active:translate-y-px disabled:opacity-40 disabled:shadow-none"
                }`}
              >
                {emailOtp.sending ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Sending verification code…</span>
                  </>
                ) : emailOtp.cooldown > 0 ? (
                  <>
                    <Loader2 size={16} className="opacity-60" />
                    <span>Resend verification code in {emailOtp.cooldown}s</span>
                  </>
                ) : emailOtp.sent ? (
                  <>
                    <Mail size={16} />
                    <span>Resend verification code</span>
                  </>
                ) : (
                  <>
                    <Mail size={16} />
                    <span>Send verification code</span>
                  </>
                )}
              </button>
            )}

            {/* Premium OTP card */}
            {emailOtp.sent && !emailOtp.verified && (
              <div className="mt-3 rounded-2xl bg-gradient-to-br from-[#0f1828] to-[#0a1119] border border-[#19a55c]/20 p-4 sm:p-5 space-y-3.5 shadow-[0_8px_30px_rgba(0,0,0,0.4)]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#19a55c]/15 border border-[#19a55c]/30">
                        <Mail size={13} className="text-[#21c46e]" />
                      </span>
                      <span className="text-[13.5px] font-bold text-white">Enter verification code</span>
                    </div>
                    <p className="text-[11.5px] text-white/55 leading-snug pl-9">
                      We sent a 6-digit code to{" "}
                      <span className="text-white/85 font-semibold break-all">{form.email}</span>
                    </p>
                  </div>
                  {emailOtp.expiresIn > 0 && (
                    <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-300 text-[11px] font-bold font-mono tracking-wider">
                      {fmtMmSs(emailOtp.expiresIn)}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between gap-1.5 sm:gap-2">
                  {emailOtp.code.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => { otpCellRefs.current[i] = el; }}
                      data-testid={`register-otp-cell-${i}`}
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      maxLength={1}
                      autoComplete="one-time-code"
                      value={digit}
                      onChange={(e) => handleOtpCellChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpCellKey(i, e)}
                      onPaste={i === 0 ? handleOtpPaste : undefined}
                      disabled={emailOtp.verifying}
                      className={`w-full max-w-[52px] h-13 sm:h-14 rounded-xl text-center text-2xl font-extrabold font-mono caret-[#21c46e] transition-all duration-150 disabled:opacity-50 ${
                        digit
                          ? "bg-[#19a55c]/12 border-2 border-[#19a55c]/60 text-[#21c46e] shadow-[0_0_0_3px_rgba(33,196,110,0.12)]"
                          : "bg-[#0a1119] border-2 border-white/8 text-white hover:border-white/20"
                      } focus:border-[#21c46e] focus:outline-none focus:ring-4 focus:ring-[#21c46e]/15`}
                      style={{ minHeight: 52 }}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between text-[11.5px] pt-1">
                  <span className="text-white/45">
                    {emailOtp.verifying ? (
                      <span className="inline-flex items-center gap-1.5 text-amber-300">
                        <Loader2 size={11} className="animate-spin" /> Verifying…
                      </span>
                    ) : (
                      "No code received?"
                    )}
                  </span>
                  <button
                    type="button"
                    data-testid="register-email-resend-link"
                    onClick={sendEmailOtp}
                    disabled={emailOtp.cooldown > 0 || emailOtp.sending}
                    className="font-bold text-[#21c46e] hover:text-emerald-300 disabled:text-white/30 disabled:cursor-not-allowed transition"
                  >
                    {emailOtp.cooldown > 0 ? `Resend in ${emailOtp.cooldown}s` : "Resend code"}
                  </button>
                </div>

                {emailOtp.devCode && (
                  <p className="text-[10.5px] text-amber-300/70 pt-1 mt-1 border-t border-white/5">
                    Dev mode (email gateway not configured) — code:{" "}
                    <b className="font-mono text-amber-200">{emailOtp.devCode}</b>
                  </p>
                )}
              </div>
            )}

            {/* Verified banner */}
            {emailOtp.verified && (
              <div
                data-testid="register-email-verified"
                className="mt-3 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500/12 to-emerald-500/5 border border-emerald-400/35 text-emerald-200 text-[13px] font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-400/40">
                  <CheckCircle2 size={15} className="text-emerald-300" />
                </span>
                <span className="flex-1">Email verified successfully</span>
                <button
                  type="button"
                  data-testid="register-email-change-btn"
                  onClick={() =>
                    setEmailOtp({ sent: false, verified: false, sending: false, verifying: false, code: ["", "", "", "", "", ""], cooldown: 0, expiresIn: 0, devCode: "", error: "" })
                  }
                  className="text-[11px] text-white/55 hover:text-white underline decoration-dotted underline-offset-2"
                >
                  Change
                </button>
              </div>
            )}

            {emailOtp.error && (
              <div
                data-testid="register-email-otp-error"
                className="mt-1.5 text-[12px] font-semibold text-rose-300 flex items-center gap-1.5"
              >
                <AlertCircle size={13} /> {emailOtp.error}
              </div>
            )}
          </Field>
        )}

        {/* Refer code / Promo code (single field — backend auto-detects) */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowRefer((s) => !s)}
            data-testid="toggle-refer-code"
            className="flex items-center gap-2 text-sm text-white/85 hover:text-white"
          >
            <Tag size={14} className="text-[#19a55c]" />
            <span>Refer code / Promo code (Optional)</span>
            <ChevronDown
              size={16}
              className={`transition-transform ${showRefer ? "rotate-180" : ""}`}
            />
          </button>
          {showRefer && (
            <div className="flex items-stretch gap-2">
              <input
                data-testid="register-refer-input"
                type="text"
                placeholder="Enter refer code or promo code"
                value={form.refer_code}
                onChange={onChange("refer_code")}
                className={`${inputCls} flex-1 min-w-0`}
              />
              <button
                type="button"
                onClick={() => setQrOpen(true)}
                data-testid="register-refer-scan-btn"
                title="Scan QR code"
                aria-label="Scan QR code"
                className="shrink-0 h-12 px-3 rounded-lg bg-[#19a55c]/15 hover:bg-[#19a55c]/25 border border-[#19a55c]/45 text-[#21c46e] inline-flex items-center gap-1.5 font-bold text-[12.5px] transition active:scale-95"
              >
                <QrCode size={16} />
                <span className="hidden xs:inline sm:inline">Scan</span>
              </button>
            </div>
          )}
        </div>

        {error && (
          <div
            role="alert"
            data-testid="register-error"
            className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}

        <div className="flex justify-center" data-testid="register-captcha">
          <TurnstileWidget onVerify={setCaptchaToken} resetSignal={captchaReset} />
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="register-submit-btn"
          className="w-full h-12 rounded-lg bg-[#19a55c] hover:bg-[#21c46e] active:bg-[#168f4f] text-white font-bold text-lg transition-colors flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_8px_24px_rgba(25,165,92,0.45)]"
        >
          {submitting ? <Loader2 className="animate-spin" size={20} /> : "Continue"}
        </button>

        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="register-terms">
          <input
            type="checkbox"
            data-testid="register-terms-checkbox"
            className="sr-only peer"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span
            aria-hidden="true"
            className={`mt-0.5 h-6 w-6 rounded flex items-center justify-center transition-colors shrink-0 ${
              accepted ? "bg-[#19a55c]" : "bg-[#1a2030] border border-white/20"
            }`}
          >
            {accepted && <Check size={16} className="text-white" />}
          </span>
          <span className="text-sm text-white/80 leading-snug" style={{ fontFamily: '"Hind Siliguri","Noto Sans Bengali",system-ui,sans-serif' }}>
            আমি নিশ্চিত করছি যে আমার বয়স <strong className="text-white">১৮ বছর বা তার বেশি</strong> এবং আমি{" "}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="terms-link"
              className="underline text-white hover:text-[#19a55c]"
              onClick={(e) => e.stopPropagation()}
            >
              Terms &amp; Conditions
            </Link>{" "}
            পড়েছি ও সম্পূর্ণ সম্মত আছি।
          </span>
        </label>
      </form>

      {/* QR scan modal — populates the refer_code field on a successful scan */}
      <QRScanModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        parse={parseScannedQR}
        onResult={(code) => {
          if (!code) return;
          setForm((f) => ({ ...f, refer_code: code }));
          setShowRefer(true);
          storeRefCode(code);
          setQrOpen(false);
          toast.success(`Code ${code} added`);
        }}
      />

      {/* One-Click Signup — auto-generated credentials modal (premium redesign) */}
      {quickCreds && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="quick-signup-creds-modal"
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-[rgba(4,10,18,0.82)] backdrop-blur-md animate-[fadeIn_.18s_ease-out]"
          style={{ animationFillMode: "both" }}
        >
          {/* Ambient background glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 overflow-hidden"
          >
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-[#19a55c]/12 blur-[120px]" />
            <div className="absolute -bottom-40 left-1/3 w-[420px] h-[420px] rounded-full bg-emerald-500/8 blur-[100px]" />
          </div>

          <div
            className="relative w-full max-w-[440px] max-h-[92vh] overflow-y-auto bg-gradient-to-b from-[#101927] to-[#0a1220] border border-white/10 rounded-2xl shadow-[0_30px_90px_-15px_rgba(0,0,0,0.9),0_0_0_1px_rgba(25,165,92,0.15)] animate-[popIn_.28s_cubic-bezier(.16,1.2,.36,1)]"
            style={{ animationFillMode: "both", fontFamily: '"Hind Siliguri","Noto Sans Bengali",system-ui,sans-serif' }}
          >
            {/* Top accent line */}
            <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#21c46e] to-transparent opacity-70" />

            {/* Header */}
            <div className="relative px-6 pt-7 pb-5 border-b border-white/[0.06]">
              <div className="flex items-start gap-4">
                {/* Animated success glyph */}
                <div className="relative shrink-0">
                  <span className="absolute inset-0 rounded-full bg-[#19a55c]/30 blur-md animate-pulse" aria-hidden="true" />
                  <span className="absolute -inset-1 rounded-full border border-[#19a55c]/40" aria-hidden="true" />
                  <div className="relative h-12 w-12 rounded-full bg-gradient-to-br from-[#21c46e] to-[#0e7c3f] flex items-center justify-center shadow-[0_10px_25px_-5px_rgba(25,165,92,0.7)]">
                    <CheckCircle2 size={26} className="text-white drop-shadow" strokeWidth={2.5} />
                  </div>
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-black tracking-widest uppercase">
                      <Sparkles size={10} /> সফল
                    </span>
                  </div>
                  <h2 className="text-white font-black text-[19px] leading-tight">
                    আপনার অ্যাকাউন্ট প্রস্তুত
                  </h2>
                  <p className="text-[12.5px] text-white/55 leading-snug mt-0.5">
                    এটাই আপনার <strong className="text-white/85">একমাত্র</strong> লগইন তথ্য — এগুলো সেভ করে তারপর এগিয়ে যান।
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              {/* Welcome bonus (if any) */}
              {quickCreds.bonus > 0 && (
                <div
                  data-testid="quick-signup-bonus"
                  className="relative overflow-hidden rounded-xl px-4 py-3 bg-gradient-to-r from-emerald-500/12 via-emerald-500/8 to-transparent border border-emerald-500/25 flex items-center gap-3"
                >
                  <div className="h-9 w-9 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0">
                    <Gift size={18} className="text-emerald-300" />
                  </div>
                  <div className="flex-1">
                    <div className="text-emerald-100 font-black text-[15px] leading-tight">
                      BDT {Number(quickCreds.bonus).toFixed(2)} যোগ হয়েছে
                    </div>
                    <div className="text-emerald-300/70 text-[11px] font-medium">ওয়েলকাম বোনাস · এখনই খেলার জন্য প্রস্তুত</div>
                  </div>
                </div>
              )}

              {/* Credential rows */}
              <div className="space-y-3">
                {/* Username */}
                <div className="group">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="inline-flex items-center gap-1.5 text-[10.5px] font-black tracking-[0.14em] text-white/45 uppercase">
                      <User size={11} /> ইউজারনেম
                    </label>
                    <span className="text-[10px] text-white/30 font-mono">{quickCreds.username?.length || 0} অক্ষর</span>
                  </div>
                  <div className="relative">
                    <div
                      data-testid="quick-signup-username"
                      className="h-[52px] rounded-xl pl-4 pr-14 bg-[#0b1220] border border-white/10 group-hover:border-[#19a55c]/40 focus-within:border-[#19a55c]/60 transition-colors flex items-center font-mono text-white font-bold text-[15.5px] tracking-[0.02em] select-all cursor-text"
                    >
                      {quickCreds.username}
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(quickCreds.username, "Username")}
                      data-testid="quick-signup-copy-username"
                      aria-label="Copy username"
                      className={`absolute right-1.5 top-1/2 -translate-y-1/2 h-10 w-10 rounded-lg flex items-center justify-center active:scale-95 transition-all ${
                        quickCopiedField === "Username"
                          ? "bg-emerald-500 text-white shadow-[0_4px_14px_rgba(25,165,92,0.55)]"
                          : "bg-white/[0.06] hover:bg-[#19a55c]/25 text-white/70 hover:text-[#21c46e]"
                      }`}
                    >
                      {quickCopiedField === "Username" ? <Check size={16} strokeWidth={3} /> : <Copy size={15} />}
                    </button>
                  </div>
                </div>

                {/* Password */}
                <div className="group">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="inline-flex items-center gap-1.5 text-[10.5px] font-black tracking-[0.14em] text-white/45 uppercase">
                      <Lock size={11} /> পাসওয়ার্ড
                    </label>
                    <span className="text-[10px] text-white/30 font-mono">{quickCreds.password?.length || 0} অক্ষর</span>
                  </div>
                  <div className="relative">
                    <div
                      data-testid="quick-signup-password"
                      className="h-[52px] rounded-xl pl-4 pr-24 bg-[#0b1220] border border-white/10 group-hover:border-[#19a55c]/40 focus-within:border-[#19a55c]/60 transition-colors flex items-center font-mono text-white font-bold text-[15.5px] tracking-[0.05em] select-all cursor-text"
                    >
                      {quickShowPw ? quickCreds.password : "•".repeat(quickCreds.password?.length || 0)}
                    </div>
                    <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setQuickShowPw((v) => !v)}
                        data-testid="quick-signup-toggle-pw"
                        aria-label={quickShowPw ? "Hide password" : "Show password"}
                        className="h-10 w-10 rounded-lg flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.09] text-white/60 hover:text-white transition"
                      >
                        {quickShowPw ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(quickCreds.password, "Password")}
                        data-testid="quick-signup-copy-password"
                        aria-label="Copy password"
                        className={`h-10 w-10 rounded-lg flex items-center justify-center active:scale-95 transition-all ${
                          quickCopiedField === "Password"
                            ? "bg-emerald-500 text-white shadow-[0_4px_14px_rgba(25,165,92,0.55)]"
                            : "bg-white/[0.06] hover:bg-[#19a55c]/25 text-white/70 hover:text-[#21c46e]"
                        }`}
                      >
                        {quickCopiedField === "Password" ? <Check size={16} strokeWidth={3} /> : <Copy size={15} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Secondary actions */}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  type="button"
                  onClick={copyAllCreds}
                  data-testid="quick-signup-copy-all"
                  className="h-10 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-white/85 font-semibold text-[12.5px] inline-flex items-center justify-center gap-1.5 active:scale-[0.98] transition"
                >
                  <Copy size={13} /> দুটোই কপি করুন
                </button>
                <button
                  type="button"
                  onClick={downloadCreds}
                  data-testid="quick-signup-download"
                  className="h-10 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-white/85 font-semibold text-[12.5px] inline-flex items-center justify-center gap-1.5 active:scale-[0.98] transition"
                >
                  <Download size={13} /> ছবি হিসেবে সেভ করুন
                </button>
              </div>

              {/* Security notice */}
              <div className="relative rounded-xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/[0.09] to-transparent" aria-hidden="true" />
                <div className="relative flex gap-2.5 px-3.5 py-3 border border-amber-500/25 rounded-xl">
                  <ShieldAlert size={16} className="text-amber-300 shrink-0 mt-0.5" />
                  <div className="text-[12px] leading-snug text-amber-100/90">
                    <div className="font-bold text-amber-200 mb-0.5">এখনই সেভ করুন — পুনরুদ্ধার সম্ভব নয়</div>
                    <div className="text-amber-100/70">
                      এই অ্যাকাউন্টে ফোন বা ইমেইল যুক্ত নেই। তথ্য হারিয়ে গেলে আপনি আর লগইন করতে পারবেন না।
                    </div>
                  </div>
                </div>
              </div>

              {/* Ack + Continue */}
              <div className="pt-1 space-y-3">
                <label
                  className="flex items-start gap-2.5 cursor-pointer select-none group"
                  data-testid="quick-signup-ack-row"
                >
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={quickAck}
                    onChange={(e) => setQuickAck(e.target.checked)}
                    data-testid="quick-signup-ack-checkbox"
                  />
                  <span
                    aria-hidden="true"
                    className={`mt-[1px] h-[18px] w-[18px] rounded-[5px] flex items-center justify-center shrink-0 transition-all ${
                      quickAck
                        ? "bg-[#19a55c] border-2 border-[#19a55c] shadow-[0_0_0_3px_rgba(25,165,92,0.18)]"
                        : "bg-transparent border-2 border-white/25 group-hover:border-white/45"
                    }`}
                  >
                    {quickAck && <Check size={12} strokeWidth={3.5} className="text-white" />}
                  </span>
                  <span className="text-[12.5px] text-white/80 leading-snug">
                    আমি নিশ্চিত করছি — আমি আমার ইউজারনেম ও পাসওয়ার্ড সেভ করেছি এবং জানি এগুলো হারিয়ে গেলে পুনরুদ্ধার করা যাবে না।
                  </span>
                </label>

                <button
                  type="button"
                  onClick={finishQuickSignup}
                  disabled={!quickAck}
                  data-testid="quick-signup-continue"
                  className="relative w-full h-[52px] rounded-xl overflow-hidden font-black text-[15px] transition-all disabled:opacity-45 disabled:cursor-not-allowed group"
                >
                  <span
                    aria-hidden="true"
                    className={`absolute inset-0 transition-all ${
                      quickAck
                        ? "bg-gradient-to-r from-[#0e7c3f] via-[#19a55c] to-[#21c46e] shadow-[0_10px_28px_-6px_rgba(25,165,92,0.65)]"
                        : "bg-[#1a2030]"
                    }`}
                  />
                  {quickAck && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full duration-700"
                    />
                  )}
                  <span className="relative text-white inline-flex items-center justify-center gap-2">
                    Continue to Dashboard
                    <span aria-hidden="true" className="text-lg leading-none">→</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthShell>
  );
}
